import { gapi } from 'gapi-script';
import { gapiConfig, config } from '../assets/config';
import { getIdToken } from './authTokenStorage';
import i18n from './i18n';

/**
 * createFolderPath で解決したフォルダIDのキャッシュ（モジュール単位）
 * キー: `共有ドライブ名|親フォルダID|パス`。使用前に存在確認を行い、削除・ゴミ箱移動されていれば再解決する。
 */
const folderIdCache = new Map<string, string>();

/** シート名に特殊文字（括弧、スペース等）が含まれる場合、シングルクォートで囲む（clear と同じ規則） */
const quoteSheetName = (sheetName: string): string =>
  sheetName.includes('(') || sheetName.includes(')') || sheetName.includes(' ')
    ? `'${sheetName}'`
    : sheetName;

/* Google Identity Services (GIS) — ランタイムで index.html から読み込まれる */
declare const google: any;

/** GIS トークンクライアント（モジュール単位シングルトン） */
let tokenClient: any = null;

/** 現在の OAuth アクセストークン情報 */
let currentToken: { access_token: string; expires_at: number; scope: string } | null = null;

// ---------------------------------------------------------------------------
// トークン永続化（localStorage）
// ページリロード時に GIS ポップアップが一瞬表示される問題を回避するため、
// 有効なトークンを localStorage に保存しリロード後も再利用する。
// アクセストークンのみ保存（リフレッシュトークンは GIS では発行されない）。
// ---------------------------------------------------------------------------
const GIS_TOKEN_STORAGE_KEY = 'gis_drive_token';

function persistToken(token: { access_token: string; expires_at: number; scope: string }): void {
  try {
    localStorage.setItem(GIS_TOKEN_STORAGE_KEY, JSON.stringify(token));
  } catch {
    // ストレージ容量超過等は無視（メモリ上のトークンで動作継続）
  }
}

function clearPersistedToken(): void {
  localStorage.removeItem(GIS_TOKEN_STORAGE_KEY);
}

/**
 * localStorage からトークンを復元する。
 * 期限切れ・パース失敗の場合は null を返す。
 */
function loadPersistedToken(): { access_token: string; expires_at: number; scope: string } | null {
  try {
    const raw = localStorage.getItem(GIS_TOKEN_STORAGE_KEY);
    if (!raw) return null;
    const token = JSON.parse(raw) as { access_token: string; expires_at: number; scope: string };
    // scope を含まない古い形式のトークンは無効扱いとし、再認証フローに入らせる
    if (
      typeof token.access_token !== 'string' ||
      typeof token.expires_at !== 'number' ||
      typeof token.scope !== 'string'
    ) return null;
    // 残り60秒未満は無効扱い
    if (Date.now() >= token.expires_at - 60 * 1000) {
      clearPersistedToken();
      return null;
    }
    return token;
  } catch {
    clearPersistedToken();
    return null;
  }
}

// モジュール読み込み時にトークンを復元（gapi.client.setToken は initGapi() 内で行う）
currentToken = loadPersistedToken();

export type DriveItemType = 'file' | 'folder';

/**
 * セル書式設定の種類
 */
export type CellFormatType = {
  /** 数値フォーマット */
  numberFormat?: {
    type: "TEXT" | "NUMBER" | "PERCENT" | "CURRENCY" | "DATE" | "TIME" | "DATE_TIME" | "SCIENTIFIC";
    pattern?: string;
  };
  /** 背景色 */
  backgroundColor?: { red?: number; green?: number; blue?: number; alpha?: number };
  /** テキストフォーマット */
  textFormat?: {
    foregroundColor?: { red?: number; green?: number; blue?: number; alpha?: number };
    fontFamily?: string;
    fontSize?: number;
    bold?: boolean;
    italic?: boolean;
    strikethrough?: boolean;
    underline?: boolean;
  };
  /** 水平方向の配置 */
  horizontalAlignment?: "LEFT" | "CENTER" | "RIGHT";
  /** 垂直方向の配置 */
  verticalAlignment?: "TOP" | "MIDDLE" | "BOTTOM";
  /** 折返し方法 */
  wrapStrategy?: "OVERFLOW_CELL" | "LEGACY_WRAP" | "CLIP" | "WRAP";
};

/**
 * セル範囲と書式設定のペア
 */
export type CellFormatRange = {
  /** シートID */
  sheetId: number;
  /** 開始行（1始まり） */
  startRow: number;
  /** 終了行（1始まり、含む） */
  endRow: number;
  /** 開始列（1始まり、A=1） */
  startCol: number;
  /** 終了列（1始まり、含む） */
  endCol: number;
  /** 書式設定 */
  format: CellFormatType;
};

/**
 * セルに設定する数式
 */
export type CellFormula = {
  /** シートID */
  sheetId: number;
  /** 行（1始まり） */
  row: number;
  /** 列（1始まり、A=1） */
  col: number;
  /** 数式（=で始まる） */
  formula: string;
};

/**
 * データ入力規則（プルダウン）の設定
 */
export type DataValidationRange = {
  /** シートID */
  sheetId: number;
  /** 開始行（1始まり） */
  startRow: number;
  /** 終了行（1始まり、含む） */
  endRow: number;
  /** 開始列（1始まり、A=1） */
  startCol: number;
  /** 終了列（1始まり、含む） */
  endCol: number;
  /** プルダウンの選択肢 */
  values: string[];
  /** 入力を選択肢のみに制限するか（デフォルト: true） */
  strict?: boolean;
  /** ドロップダウンを表示するか（デフォルト: true） */
  showDropdown?: boolean;
};

/**
 * データ入力規則（他セル範囲を選択肢として参照するプルダウン、ONE_OF_RANGE）の設定
 */
export type RangeValidationRange = {
  /** シートID */
  sheetId: number;
  /** 開始行（1始まり） */
  startRow: number;
  /** 終了行（1始まり、含む） */
  endRow: number;
  /** 開始列（1始まり、A=1） */
  startCol: number;
  /** 終了列（1始まり、含む） */
  endCol: number;
  /** 選択肢として参照するセル範囲（例: "SheetData!$C$28:$E$28"） */
  sourceRange: string;
  /** 入力を選択肢のみに制限するか（デフォルト: true） */
  strict?: boolean;
  /** ドロップダウンを表示するか（デフォルト: true） */
  showDropdown?: boolean;
};

/**
 * 数値範囲のデータ入力規則設定
 */
export type NumberValidationRange = {
  /** シートID */
  sheetId: number;
  /** 開始行（1始まり） */
  startRow: number;
  /** 終了行（1始まり、含む） */
  endRow: number;
  /** 開始列（1始まり、A=1） */
  startCol: number;
  /** 終了列（1始まり、含む） */
  endCol: number;
  /** 最小値 */
  min: number;
  /** 最大値 */
  max: number;
  /** セル選択時のヘルプテキスト */
  helpText?: string;
};

/**
 * 条件付き書式の設定
 */
export type ConditionalFormatRange = {
  /** シートID */
  sheetId: number;
  /** 開始行（1始まり） */
  startRow: number;
  /** 終了行（1始まり、含む） */
  endRow: number;
  /** 開始列（1始まり、A=1） */
  startCol: number;
  /** 終了列（1始まり、含む） */
  endCol: number;
  /** 条件を満たすときに適用する背景色 */
  backgroundColor?: { red?: number; green?: number; blue?: number; alpha?: number };
  /** 条件を満たすときに適用する文字色 */
  textColor?: { red?: number; green?: number; blue?: number; alpha?: number };
  /** カスタム数式（例: "=$C$8<>\"なし\""） */
  customFormula: string;
};

/**
 * 保護範囲の設定
 */
export type ProtectedRangeConfig = {
  /** シートID */
  sheetId: number;
  /** 開始行（1始まり） */
  startRow: number;
  /** 終了行（1始まり、含む） */
  endRow: number;
  /** 開始列（1始まり、A=1） */
  startCol: number;
  /** 終了列（1始まり、含む） */
  endCol: number;
  /** 警告文言 */
  warningDescription?: string;
};

/**
 * 日付範囲のデータ入力規則設定
 */
export type DateValidationRange = {
  /** シートID */
  sheetId: number;
  /** 開始行（1始まり） */
  startRow: number;
  /** 終了行（1始まり、含む） */
  endRow: number;
  /** 開始列（1始まり、A=1） */
  startCol: number;
  /** 終了列（1始まり、含む） */
  endCol: number;
  /** 開始日（YYYY-MM-DD形式） */
  startDate: string;
  /** 終了日（YYYY-MM-DD形式） */
  endDate: string;
  /** セル選択時のヘルプテキスト */
  helpText?: string;
};

/**
 * Google Drive操作ログをバックエンドに送信する。
 * ログ送信の失敗はGoogle Drive操作に影響させない（非同期・try-catch）。
 */
const sendDriveOperationLog = async (params: {
  operationType: string;
  targetFileId?: string;
  targetFileName?: string;
  fileSize?: number;
  projectId?: string;
  details?: string;
  resultStatus: string;
}): Promise<void> => {
  try {
    const idToken = getIdToken();
    if (!idToken || !config.appSyncUrl) return;

    await fetch(config.appSyncUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': idToken,
      },
      body: JSON.stringify({
        query: `mutation logDriveOperation($input: LogDriveOperationInput!) {
          logDriveOperation(input: $input) { status }
        }`,
        variables: { input: params },
      }),
    });
  } catch (e) {
    console.error('Failed to send drive operation log:', e);
  }
};

/**
 * Google Drive APIクエリ文字列内の特殊文字をエスケープする。
 * シングルクォートとバックスラッシュをエスケープし、クエリインジェクションを防止する。
 */
const escapeDriveQuery = (str: string): string => {
  return str.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
};

export default class GoogleAPI {
  /** スコープ：Googleドライブ Googleスプレッドシート */
  // drive.file: アプリが作成したファイルのみにアクセスを限定（権限制御強化）
  public static readonly SCOPES = 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/spreadsheets';

  /** ドックス：Googleドライブ Googleスプレッドシート */
  public static readonly DISCOVERY_DOCS = [
    'https://www.googleapis.com/discovery/v1/apis/drive/v3/rest',
    "https://sheets.googleapis.com/$discovery/rest?version=v4"
  ];

  /**
   * Google APIを初期化する。
   * gapi.client（Drive/Sheets）と GIS トークンクライアントを初期化する。
   * @returns true：成功、false：失敗
   */
  public async initGapi() {
    try {
      console.log("Google API initialize start");
      // auth2 は不要 — GIS に移行済みのため "client" のみロード
      await new Promise((resolve) => gapi.load("client", resolve));

      await gapi.client.init({
        discoveryDocs: GoogleAPI.DISCOVERY_DOCS,
      });

      // GIS トークンクライアントを初期化（モジュール単位シングルトン）
      if (!tokenClient) {
        tokenClient = google.accounts.oauth2.initTokenClient({
          client_id: gapiConfig.clientId,
          scope: GoogleAPI.SCOPES,
          callback: '',
        });
      }

      // gapi.client.init() 完了後に復元済みトークンをセット
      if (currentToken) {
        gapi.client.setToken({ access_token: currentToken.access_token });
      }

      console.log("Google API initialize success");
      return true;
    } catch (error) {
      console.error("Google API initialize error:", error);
      return false;
    } finally {
      console.log("Google API initialize end");
    }
  }

  /**
   * 認証判定を行い、必要なら認証を行う。
   * 有効なトークンが既にある場合はポップアップを表示しない。
   * @returns true：成功、false：失敗
   */
  public async signIn(): Promise<boolean> {
    if (!tokenClient) return false;

    // 有効なトークンが残っており、必要なスコープをすべて満たしていればそのまま返す
    if (
      currentToken &&
      Date.now() < currentToken.expires_at - 60 * 1000 &&
      this.hasRequiredScopes()
    ) {
      gapi.client.setToken({ access_token: currentToken.access_token });
      console.info("The user is already signed in.");
      return true;
    }

    // サイレント取得を試みる（Googleセッションが有効であればポップアップなし）
    const silent = await this.tryRequestToken('');
    if (silent) return true;

    // サイレント失敗時のみ同意画面を表示
    console.info("Silent token acquisition failed. Showing consent screen.");
    return this.tryRequestToken('consent');
  }

  /**
   * GIS トークンリクエストを発行し、成功時に currentToken を更新する。
   * @param prompt '' でサイレント取得、'consent' で同意画面を表示
   */
  private tryRequestToken(prompt: '' | 'consent'): Promise<boolean> {
    return new Promise((resolve) => {
      tokenClient.callback = (response: any) => {
        if (response.error) {
          console.error("SignIn error:", response);
          resolve(false);
          return;
        }
        const newToken = {
          access_token: response.access_token,
          expires_at: Date.now() + response.expires_in * 1000,
          scope: response.scope,
        };
        currentToken = newToken;
        persistToken(newToken);
        gapi.client.setToken({ access_token: newToken.access_token });
        console.info("SignIn success");
        resolve(true);
      };
      tokenClient.requestAccessToken({ prompt });
    });
  }

  /**
   * サインアウトを行う。
   * @returns true：成功、false：失敗
   */
  public async signOut(): Promise<boolean> {
    try {
      if (currentToken?.access_token) {
        google.accounts.oauth2.revoke(currentToken.access_token, () => { });
        currentToken = null;
        clearPersistedToken();
        folderIdCache.clear();
        gapi.client.setToken(null as any);
        console.info("SignOut success");
      }
      return true;
    } catch (error) {
      console.error("SignOut error:", error);
      return false;
    }
  }

  /**
   * アクセストークンの有効期限チェックおよびサイレントリフレッシュ。
   * リフレッシュに失敗した場合は false を返す（呼び出し側が signIn() を実行）。
   * @returns true：有効期限内またはリフレッシュ成功、false：サインインが必要
   */
  public async ensureValidToken(): Promise<boolean> {
    if (!tokenClient) return false;

    // 残り60秒以上あれば有効
    if (currentToken && Date.now() < currentToken.expires_at - 60 * 1000) {
      return true;
    }

    // サイレントリフレッシュ（prompt: '' はユーザー操作なしで再取得を試みる）
    return this.tryRequestToken('');
  }

  /**
   * スコープを満たしているかを判定する。
   * GIS（Google Identity Services）のトークンレスポンスに含まれる scope（許諾済みスコープの
   * スペース区切り文字列）を currentToken に保持しておき、それと比較して判定する。
   * @returns true：スコープを満たしている。false：スコープを満たしていない。⇒ 再認証が必要
   */
  private hasRequiredScopes(): boolean {
    if (!currentToken?.scope) return false;
    const grantedScopes = currentToken.scope.split(' ');
    return GoogleAPI.SCOPES.split(' ').every(scope => grantedScopes.includes(scope));
  };

  /**
   * スプレッドシート内のすべてのシート名を取得する。
   * @param spreadsheetId スプレッドシートID
   * @returns シート名の一覧
   */
  public async getSheetNames(spreadsheetId: string): Promise<string[]> {
    try {
      // スプレッドシート情報を取得
      const spreadsheetInfo = await this.getSpreadSheetInfo(spreadsheetId);

      // 各シートのプロパティから名前を抽出
      const sheetNames = spreadsheetInfo.sheets.map((sheet: any) => sheet.properties.title);

      return sheetNames;
    } catch (error) {
      console.error('Error while fetching sheet names:', error);
      throw error;
    }
  }

  /**
   * スプレッドシートの最初のシート名を取得する。
   * (ロケールによってデフォルトシート名が異なる問題回避のため)
   * @param spreadsheetId スプレッドシートID
   * @returns 最初のシート名（取得できない場合はnull）
   */
  public async getFirstSheetName(spreadsheetId: string): Promise<string | null> {
    try {
      const spreadsheetInfo = await this.getSpreadSheetInfo(spreadsheetId);
      return spreadsheetInfo.sheets?.[0]?.properties?.title ?? null;
    } catch (error) {
      console.error('Error while fetching first sheet name:', error);
      return null;
    }
  }

  /**
   * 先頭シートのsheetId（グリッドID）を取得する。
   * repeatCell等、シート名ではなく数値IDを要求するAPIで使用する。
   * @param spreadsheetId スプレッドシートID
   * @returns 先頭シートのsheetId（取得できない場合はnull）
   */
  public async getFirstSheetId(spreadsheetId: string): Promise<number | null> {
    try {
      const spreadsheetInfo = await this.getSpreadSheetInfo(spreadsheetId);
      const sheetId = spreadsheetInfo.sheets?.[0]?.properties?.sheetId;
      return typeof sheetId === "number" ? sheetId : null;
    } catch (error) {
      console.error('Error while fetching first sheet id:', error);
      return null;
    }
  }

  /**
   * 指定した共有ドライブ名に対応するIDを取得する。
   * @param sharedDriveName 共有ドライブ名
   * @returns 共有ドライブID（見つからない場合はnull）
   */
  public async getDriveIdByName(sharedDriveName?: string | null): Promise<string | null> {
    if (!(sharedDriveName || null)) {
      return null;
    }

    const res = await gapi.client.drive.drives.list({
      pageSize: 100,
    });

    const drive = res.result.drives?.find((d: any) => d.name === sharedDriveName);
    return drive?.id ?? null;
  };

  /**
   * 指定したパスの末端フォルダのIDを取得
   * @param path パス
   * @param sharedDriveId 共有ドライブID
   * @param parentId 親フォルダID
   * @returns 末端フォルダのID
   */
  private async resolvePathToFolderId(
    path: string,
    sharedDriveId?: string | null,
    parentId: string = 'root'
  ): Promise<string | null> {
    const folders = path.split('/').filter(Boolean);

    if (sharedDriveId && parentId === "root") {
      // 共有ドライブ名が指定ありかつ親フォルダIDが未指定(root)なら、ドライブIDに振り返え
      parentId = sharedDriveId ?? "";
    }
    let currentFolderId = parentId;

    for (const folderName of folders) {
      const res = await gapi.client.drive.files.list({
        q: [
          `name='${escapeDriveQuery(folderName)}'`,
          `'${currentFolderId}' in parents`,
          `mimeType = 'application/vnd.google-apps.folder'`,
          `trashed = false`,
        ].join(' and '),
        fields: 'files(id)',
        pageSize: 1,
        supportsAllDrives: true,
        includeItemsFromAllDrives: !!sharedDriveId,
        corpora: sharedDriveId ? 'drive' : 'user',
        driveId: sharedDriveId ?? undefined,
      });

      const folder = res.result.files?.[0];
      if (!folder) {
        return null;
      }
      currentFolderId = folder.id!;
    }

    return currentFolderId;
  }

  /**
   * 指定したフルパスのIDを取得
   * @param path フルパス
   * @param type ファイル("file")またはフォルダ("folder")を指定する。
   * @param sharedDriveName 共有ドライブ名（省略時はマイドライブ）
   * @param parentId 親フォルダID
   * @returns 取得したファイルまたはフォルダのID
   */
  public async getFileOrFolderId(
    path: string,
    type: DriveItemType,
    sharedDriveName?: string | null,
    parentId: string = 'root'
  ): Promise<string | null> {
    // パスを分解
    const segments = path.split('/').filter(Boolean);

    if (segments.length === 0) {
      // パスなしの場合、null
      return null;
    }

    // 共有ドライブ名が指定されていれば、ID取得
    const sharedDriveId = await this.getDriveIdByName(sharedDriveName);

    if (sharedDriveName && parentId === "root") {
      // 共有ドライブ名が指定ありかつ親フォルダIDが未指定(root)なら、ドライブIDに振り返え
      parentId = sharedDriveId ?? "";
    }

    // 分解したパスの末尾項目を取得および配列から削除
    const itemName = segments.pop()!;

    // 末尾項目以外のパス文字列にする
    const folderPath = segments.join('/');

    // 末端フォルダのID取得
    const folderId = folderPath ? await this.resolvePathToFolderId(folderPath, sharedDriveId, parentId) : parentId;

    if (!folderId) {
      console.warn('Folder in path not found:', folderPath);
      return null;
    }

    // 検索対象によってmimeTypeを切り替え
    const mimeFilter =
      type === 'folder'
        ? "mimeType = 'application/vnd.google-apps.folder'"
        : "mimeType != 'application/vnd.google-apps.folder'";

    // ファイル/フォルダのリスト取得
    const res = await gapi.client.drive.files.list({
      q: [
        `name = '${escapeDriveQuery(itemName)}'`,
        `'${folderId}' in parents`,
        mimeFilter,
        'trashed = false',                      // ゴミ箱は含めない（false）
      ].join(' and '),
      orderBy: 'modifiedTime desc',             // 更新日時を降順でソート
      fields: 'files(id, name, modifiedTime)',  // 取得フィールド
      pageSize: 1,
      supportsAllDrives: true,
      includeItemsFromAllDrives: !!sharedDriveName,
      corpora: sharedDriveId ? 'drive' : 'user',
      driveId: sharedDriveId ?? undefined,
    });

    return res.result.files?.[0]?.id ?? null;
  }

  /**
   * パスに基づき、存在しないフォルダは作成して最終フォルダのIDを返す
   * @param path パス
   * @param sharedDriveName 共有ドライブ名
   * @param parentId 親フォルダID
   * @returns 最後に作られた or 見つかったフォルダID
   */
  public async createFolderPath(path: string, sharedDriveName?: string | null, parentId: string = 'root'): Promise<string | null> {
    // キャッシュ済みのフォルダIDが有効であれば、フォルダ探索（1階層ごとのAPI呼び出し）を省略する
    const cacheKey = `${sharedDriveName ?? ''}|${parentId}|${path}`;
    const cachedId = folderIdCache.get(cacheKey);
    if (cachedId) {
      if (await this.isFolderAvailable(cachedId)) {
        sendDriveOperationLog({
          operationType: 'folder_create',
          targetFileId: cachedId,
          targetFileName: path,
          resultStatus: 'success',
          details: JSON.stringify({ sharedDriveName: sharedDriveName ?? null, cached: true }),
        });
        return cachedId;
      }
      folderIdCache.delete(cacheKey);
    }

    try {
      // パスを分解
      const segments = path.split('/').filter(Boolean);

      // 共有ドライブ名が指定されていれば、ID取得
      const sharedDriveId = await this.getDriveIdByName(sharedDriveName);

      if (sharedDriveName && parentId === "root") {
        // 共有ドライブ名が指定ありかつ親フォルダIDが未指定(root)なら、ドライブIDに振り返え
        parentId = sharedDriveId ?? "";
      }

      for (const segment of segments) {
        const id = await this.resolvePathToFolderId(segment, sharedDriveId, parentId) || "";

        if (id) {
          parentId = id;
        } else {
          // 存在しない場合、フォルダ作成
          const response = await gapi.client.drive.files.create({
            resource: {
              name: segment,
              mimeType: 'application/vnd.google-apps.folder',
              driveId: sharedDriveId ?? undefined,
              parents: parentId ? [parentId] : [],
            },
            fields: 'id, name',
            supportsAllDrives: true, // 共有ドライブ対応
          });
          parentId = response?.result?.id || "";
        }
      }

      // 最後に作られた or 見つかったフォルダID（次回以降の探索省略のためキャッシュ）
      if (parentId) {
        folderIdCache.set(cacheKey, parentId);
      }
      sendDriveOperationLog({
        operationType: 'folder_create',
        targetFileId: parentId || undefined,
        targetFileName: path,
        resultStatus: 'success',
        details: JSON.stringify({ sharedDriveName: sharedDriveName ?? null }),
      });
      return parentId || "";
    } catch (error) {
      console.log(`createFolderPath error`, error);
      sendDriveOperationLog({
        operationType: 'folder_create',
        targetFileName: path,
        resultStatus: 'error',
        details: JSON.stringify({ error: error instanceof Error ? error.message : 'Unknown error', sharedDriveName: sharedDriveName ?? null }),
      });
      return null;
    }
  };

  /**
   * フォルダが利用可能（存在し、ゴミ箱にない）かを確認する。
   * @param folderId フォルダID
   * @returns true：利用可能、false：利用不可（削除済み・ゴミ箱・権限なし等）
   */
  private async isFolderAvailable(folderId: string): Promise<boolean> {
    try {
      const res = await gapi.client.drive.files.get({
        fileId: folderId,
        fields: 'id, trashed, mimeType',
        supportsAllDrives: true,
      });
      return !res.result.trashed && res.result.mimeType === 'application/vnd.google-apps.folder';
    } catch (error) {
      console.warn('Cached folder is not available:', folderId, error);
      return false;
    }
  }

  /**
   * Googleドライブにファイルをアップロードする。
   * @param file ファイル
   * @param folderId 保存先フォルダID（未指定の場合、マイドライブ直下）
   * @returns 実行結果
   */
  public async uploadFileToDrive(file: File, folderId?: string) {
    try {
      const metadata = {
        name: file.name,
        mimeType: file.type,
        parents: folderId ? [folderId] : [],
      };

      const updateMetadata = {
        name: file.name,
        mimeType: file.type,
      };

      const token = gapi.client.getToken();
      if (!token) throw new Error("Not authenticated");
      const accessToken = token.access_token;

      // 既存のファイルを検索
      const existingFileId = await this.findFileIdByName(file.name, folderId);

      let response;
      if (existingFileId) {

        const updateForm = new FormData();
        updateForm.append('metadata', new Blob([JSON.stringify(updateMetadata)], { type: 'application/json' }));
        updateForm.append('file', file);

        // 同名のファイルが見つかった場合、ファイルを更新
        // 共有ドライブへの配置に対応するため、supportsAllDrivesをtrueに設定
        response = await fetch(`https://www.googleapis.com/upload/drive/v3/files/${existingFileId}?uploadType=multipart&supportsAllDrives=true`, {
          method: 'PATCH',
          headers: new Headers({ 'Authorization': 'Bearer ' + accessToken }),
          body: updateForm,
        });
      } else {

        const form = new FormData();
        form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
        form.append('file', file);

        // 同名ファイルが見つからない場合、新しいファイルを作成
        response = await fetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&supportsAllDrives=true', {
          method: 'POST',
          headers: new Headers({ 'Authorization': 'Bearer ' + accessToken }),
          body: form,
        });
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error uploading file: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log('File uploaded to Google Drive:', result);
      sendDriveOperationLog({
        operationType: existingFileId ? 'file_update' : 'file_upload',
        targetFileId: result?.id,
        targetFileName: file.name,
        fileSize: file.size,
        resultStatus: 'success',
        details: JSON.stringify({ folderId, mimeType: file.type }),
      });
      return result;

    } catch (error) {
      console.error('Error uploading file to Google Drive:', error);
      sendDriveOperationLog({
        operationType: 'file_upload',
        targetFileName: file.name,
        fileSize: file.size,
        resultStatus: 'error',
        details: JSON.stringify({ error: error instanceof Error ? error.message : 'Unknown error', folderId }),
      });
      throw error;
    }
  }

  /**
   * フォルダ内の同じ名前のファイルを検索する。
   * @param fileName ファイル名
   * @param folderId フォルダID
   * @returns ファイルが存在する場合フォルダID
   */
  private async findFileIdByName(fileName: string, folderId?: string): Promise<string | null> {
    try {
      const query = [
        `name='${escapeDriveQuery(fileName)}'`,
        'trashed=false',
        folderId ? `'${folderId}' in parents` : '',
      ].filter(Boolean).join(' and ');

      const response = await gapi.client.drive.files.list({
        q: query,
        fields: 'files(id, name)',
        supportsAllDrives: true,
        includeItemsFromAllDrives: !!folderId,
      });

      const files = response.result.files;
      if (files && files.length > 0) {
        return files[0].id || null;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error finding file by name:', error);
      return null;
    }
  }

  /** デフォルトシート名（ロケール非依存の固定名） */
  public static readonly DEFAULT_SHEET_NAME = "SheetData";

  /**
   * Googleスプレッドシートを新規作成する。
   * @param fileName ファイル名
   * @param folderId 保存先フォルダID（未指定の場合、マイドライブ直下）
   * @param sharedDriveName 共有ドライブ名
   * @param options.skipExistCheck true の場合、同名ファイルの存在確認を行わず常に新規作成する（既定: false）
   * @returns GoogleスプレッドシートID
   */
  public async createSpreadSheet(
    fileName: string,
    folderId?: string,
    sharedDriveName?: string | null,
    options?: { skipExistCheck?: boolean }
  ): Promise<string> {
    try {
      // 呼び出し側で既存ファイルの削除まで済ませている場合は、存在確認を省略する
      let spreadsheetId = options?.skipExistCheck
        ? null
        : await this.getFileOrFolderId(fileName, "file", sharedDriveName, folderId);
      if (spreadsheetId) {
        // 既に存在する場合、IDを返す。
        console.log(`Spreadsheet already exists spreadsheetId = [${spreadsheetId}]`);
        sendDriveOperationLog({
          operationType: 'spreadsheet_access_existing',
          targetFileId: spreadsheetId,
          targetFileName: fileName,
          resultStatus: 'success',
        });
        return spreadsheetId;
      }

      // スプレッドシート作成時に作成先は指定できない
      const response = await gapi.client.sheets.spreadsheets.create({
        resource: {
          properties: {
            title: fileName ?? i18n.t('common.message.newSpreadsheetDefaultName'),   // シートの名前
          },
        },
      });

      spreadsheetId = response.result.spreadsheetId!;

      if ((folderId || null) && (spreadsheetId || null)) {
        // ファイル移動（親フォルダIDを設定）
        await gapi.client.drive.files.update({
          fileId: spreadsheetId,
          addParents: folderId,
          fields: 'id, name, parents',
          supportsAllDrives: true,
          resource: {},
        });
      }

      // デフォルトシート名をロケール非依存の固定名にリネーム
      // （ロケールによって「Sheet1」「シート1」等異なる問題への対応）
      try {
        const spreadsheetInfo = await this.getSpreadSheetInfo(spreadsheetId!);
        const firstSheet = spreadsheetInfo.sheets?.[0];
        if (firstSheet && firstSheet.properties?.title !== GoogleAPI.DEFAULT_SHEET_NAME) {
          await gapi.client.sheets.spreadsheets.batchUpdate({
            spreadsheetId,
            resource: {
              requests: [{
                updateSheetProperties: {
                  properties: {
                    sheetId: firstSheet.properties.sheetId,
                    title: GoogleAPI.DEFAULT_SHEET_NAME,
                  },
                  fields: "title",
                },
              }],
            },
          });
          console.log(`Renamed default sheet to "${GoogleAPI.DEFAULT_SHEET_NAME}"`);
        }
      } catch (error) {
        console.warn("Failed to rename default sheet:", error);
      }

      console.log(`created a new spreadsheet spreadsheetId = [${spreadsheetId}]`);
      sendDriveOperationLog({
        operationType: 'spreadsheet_create',
        targetFileId: spreadsheetId!,
        targetFileName: fileName,
        resultStatus: 'success',
        details: JSON.stringify({ folderId, sharedDriveName: sharedDriveName ?? null }),
      });
      return spreadsheetId!;
    } catch (error) {
      sendDriveOperationLog({
        operationType: 'spreadsheet_create',
        targetFileName: fileName,
        resultStatus: 'error',
        details: JSON.stringify({ error: error instanceof Error ? error.message : 'Unknown error', folderId, sharedDriveName: sharedDriveName ?? null }),
      });
      throw error;
    }
  }

  /**
   * スプレッドシートを開くURLを返す。
   * @param spreadsheetId スプレッドシートID
   * @returns スプレッドシートを開くURL
   */
  public getOpenSpradSheetUrl(spreadsheetId: string) {
    return `https://docs.google.com/spreadsheets/d/${spreadsheetId}`;
  }

  /**
   * 指定した列インデックスをABC・・・に変換する。
   * @param col 列
   * @returns ABCなどの文字列
   */
  public columnToLetter(col: number): string {
    let temp = '';
    col = col - 1;

    while (col >= 0) {
      temp = String.fromCharCode((col % 26) + 65) + temp;
      col = Math.floor(col / 26) - 1;
    }
    return temp;
  };

  /**
   * valuesからrangeを生成する。
   * @param sheetName シート名
   * @param values 値（配列）
   * @param startRow 開始行(1～)
   * @param startCol 開始列(1～)
   * @returns range文字列
   */
  public generateRange(sheetName: string, values: string[][], startRow = 1, startCol = 1): string {
    const numRows = values.length;
    const numCols = values[0]?.length || 0;

    const startCell = this.columnToLetter(startCol) + startRow;
    const endCell = this.columnToLetter(startCol + numCols - 1) + (startRow + numRows - 1);

    // シート名に特殊文字（括弧、スペース等）が含まれる場合、シングルクォートで囲む
    const quotedName = sheetName.includes('(') || sheetName.includes(')') || sheetName.includes(' ')
      ? `'${sheetName}'`
      : sheetName;

    return `${quotedName}!${startCell}:${endCell}`;
  };

  /**
   * 指定したスプレッドシートID、シート名に値を設定する。
   * @param spreadsheetId スプレッドシートID
   * @param sheetName シート名
   * @param startRow 開始行(1～)
   * @param startCol 開始列(1～)
   * @param values 値（配列）
   */
  public async spreadSheetSetValue(spreadsheetId: string, sheetName: string, startRow = 1, startCol = 1, values: any[][]) {
    const range = this.generateRange(sheetName, values, startRow, startCol);
    await this.spreadSheetSetValueRange(spreadsheetId, range, values);
  }

  /**
   * 指定したスプレッドシートID、範囲に値を設定する。
   * @param spreadsheetId スプレッドシートID
   * @param range 範囲
   * @param values 値（配列）
   */
  public async spreadSheetSetValueRange(spreadsheetId: string, range: string, values: any[][]) {
    try {
      await gapi.client.sheets.spreadsheets.values.update({
        spreadsheetId: spreadsheetId,
        range: range,
        valueInputOption: "USER_ENTERED",
        resource: {
          values: values,
        },
      });
      sendDriveOperationLog({
        operationType: 'spreadsheet_write',
        targetFileId: spreadsheetId,
        resultStatus: 'success',
        details: JSON.stringify({ range, rows: values.length, cols: values[0]?.length ?? 0 }),
      });
    } catch (error) {
      sendDriveOperationLog({
        operationType: 'spreadsheet_write',
        targetFileId: spreadsheetId,
        resultStatus: 'error',
        details: JSON.stringify({ error: error instanceof Error ? error.message : 'Unknown error', range }),
      });
      throw error;
    }
  }

  /**
   * 指定したセル範囲に書式設定を適用する。
   * 複数の範囲に対して一括で書式設定を行う。
   * @param spreadsheetId スプレッドシートID
   * @param formatRanges 書式設定する範囲と書式の配列
   * @example
   * // パーセンテージ形式を設定
   * await googleAPI.spreadSheetSetCellFormat(spreadsheetId, [{
   *   sheetId: 123456789,
   *   startRow: 2,
   *   endRow: 10,
   *   startCol: 3,
   *   endCol: 5,
   *   format: {
   *     numberFormat: { type: "PERCENT", pattern: "0.0%" }
   *   }
   * }]);
   */
  public async spreadSheetSetCellFormat(spreadsheetId: string, formatRanges: CellFormatRange[]) {
    // batchUpdateのリクエストを作成
    const requests: any[] = formatRanges.map((formatRange) => {
      // CellFormatTypeからGoogle Sheets APIのCellFormat形式に変換
      const cellFormat: any = {};
      const fields: string[] = [];

      if (formatRange.format.numberFormat) {
        cellFormat.numberFormat = formatRange.format.numberFormat;
        fields.push("userEnteredFormat.numberFormat");
      }
      if (formatRange.format.backgroundColor) {
        cellFormat.backgroundColor = formatRange.format.backgroundColor;
        fields.push("userEnteredFormat.backgroundColor");
      }
      if (formatRange.format.textFormat) {
        cellFormat.textFormat = formatRange.format.textFormat;
        fields.push("userEnteredFormat.textFormat");
      }
      if (formatRange.format.horizontalAlignment) {
        cellFormat.horizontalAlignment = formatRange.format.horizontalAlignment;
        fields.push("userEnteredFormat.horizontalAlignment");
      }
      if (formatRange.format.verticalAlignment) {
        cellFormat.verticalAlignment = formatRange.format.verticalAlignment;
        fields.push("userEnteredFormat.verticalAlignment");
      }
      if (formatRange.format.wrapStrategy) {
        cellFormat.wrapStrategy = formatRange.format.wrapStrategy;
        fields.push("userEnteredFormat.wrapStrategy");
      }

      return {
        repeatCell: {
          range: {
            sheetId: formatRange.sheetId,
            startRowIndex: formatRange.startRow - 1, // 0始まりに変換
            endRowIndex: formatRange.endRow,         // endは含まないので+1不要
            startColumnIndex: formatRange.startCol - 1,
            endColumnIndex: formatRange.endCol,
          },
          cell: {
            userEnteredFormat: cellFormat,
          },
          fields: fields.join(","),
        },
      };
    });

    if (requests.length > 0) {
      await this.sendBatchUpdate(spreadsheetId, requests);
    }
  }

  /**
   * 指定したセルに数式を設定する。
   * 複数のセルに対して一括で数式を設定可能。
   * @param spreadsheetId スプレッドシートID
   * @param formulas 数式設定の配列
   * @example
   * // 複数セルに数式を設定
   * await googleAPI.spreadSheetSetFormulas(spreadsheetId, [
   *   { sheetId: 123456789, row: 2, col: 3, formula: "=A2+B2" },
   *   { sheetId: 123456789, row: 3, col: 3, formula: "=A3+B3" },
   *   { sheetId: 123456789, row: 10, col: 1, formula: "=SUM(A2:A9)" }
   * ]);
   */
  public async spreadSheetSetFormulas(spreadsheetId: string, formulas: CellFormula[]) {
    // batchUpdateのリクエストを作成
    const requests: any[] = formulas.map((cellFormula) => {
      return {
        updateCells: {
          range: {
            sheetId: cellFormula.sheetId,
            startRowIndex: cellFormula.row - 1,
            endRowIndex: cellFormula.row,
            startColumnIndex: cellFormula.col - 1,
            endColumnIndex: cellFormula.col,
          },
          rows: [
            {
              values: [
                {
                  userEnteredValue: {
                    formulaValue: cellFormula.formula,
                  },
                },
              ],
            },
          ],
          fields: "userEnteredValue.formulaValue",
        },
      };
    });

    if (requests.length > 0) {
      await this.sendBatchUpdate(spreadsheetId, requests);
    }
  }

  /**
   * 指定した範囲の値を取得する。
   * @param spreadsheetId スプレッドシートID
   * @param range 範囲（シート名のみで入力セルすべて取得）
   * @returns {値（配列）,範囲}
   */
  public async spreadSheetGetValues(spreadsheetId: string, range: string) {
    try {
      // シート名に特殊文字（括弧、スペース等）が含まれる場合、シングルクォートで囲む
      const quotedRange = !range.includes('!') && (range.includes('(') || range.includes(')') || range.includes(' '))
        ? `'${range}'`
        : range;
      const response = await gapi.client.sheets.spreadsheets.values.get({
        spreadsheetId,
        range: quotedRange,
        majorDimension: 'ROWS'
      });

      const result = {
        values: response.result.values,
        range: response.result.range,
      }

      sendDriveOperationLog({
        operationType: 'spreadsheet_read',
        targetFileId: spreadsheetId,
        resultStatus: 'success',
        details: JSON.stringify({ range, rows: response.result.values?.length ?? 0 }),
      });

      return result;
    } catch (error) {
      sendDriveOperationLog({
        operationType: 'spreadsheet_read',
        targetFileId: spreadsheetId,
        resultStatus: 'error',
        details: JSON.stringify({ error: error instanceof Error ? error.message : 'Unknown error', range }),
      });
      throw error;
    }
  }

  /**
   * スプレッドシート情報を取得する。
   * @param spreadsheetId スプレッドシートID
   * @returns スプレッドシート情報
   */
  public async getSpreadSheetInfo(spreadsheetId: string): Promise<any> {
    const res = await gapi.client.sheets.spreadsheets.get({ spreadsheetId });
    return res.result;
  }

  /**
   * spreadsheets.batchUpdate を送信する。
   * GoogleAPIBatchCollector で上書きし、送信せずに溜めることができる。
   * @param spreadsheetId スプレッドシートID
   * @param requests batchUpdate リクエスト
   */
  protected async sendBatchUpdate(spreadsheetId: string, requests: any[]): Promise<void> {
    await gapi.client.sheets.spreadsheets.batchUpdate({
      spreadsheetId,
      resource: {
        requests,
      },
    });
  }

  /**
   * 指定したシート構成でスプレッドシートを新規作成する（存在確認なし・常に新規作成）。
   * 作成時にシートを指定するため、デフォルトシートのリネームやシート追加の呼び出しが不要。
   * @param fileName ファイル名
   * @param sheetNames 作成するシート名（指定順に並ぶ）
   * @param folderId 保存先フォルダID（未指定の場合、マイドライブ直下）
   * @returns スプレッドシートIDと、シート名→シートIDの対応表
   */
  public async createSpreadSheetWithSheets(
    fileName: string,
    sheetNames: string[],
    folderId?: string
  ): Promise<{ spreadsheetId: string; sheetNameToId: Record<string, number> }> {
    try {
      const response = await gapi.client.sheets.spreadsheets.create({
        fields: 'spreadsheetId,sheets.properties(sheetId,title)',
        resource: {
          properties: {
            title: fileName,
          },
          sheets: sheetNames.map((title, index) => ({ properties: { title, index } })),
        },
      });
      const spreadsheetId = response.result.spreadsheetId!;

      const sheetNameToId: Record<string, number> = {};
      for (const sheet of response.result.sheets ?? []) {
        if (sheet.properties?.title && sheet.properties?.sheetId !== undefined) {
          sheetNameToId[sheet.properties.title] = sheet.properties.sheetId;
        }
      }

      if (folderId) {
        // ファイル移動（親フォルダIDを設定）
        await gapi.client.drive.files.update({
          fileId: spreadsheetId,
          addParents: folderId,
          fields: 'id, name, parents',
          supportsAllDrives: true,
          resource: {},
        });
      }

      console.log(`created a new spreadsheet with sheets spreadsheetId = [${spreadsheetId}]`);
      sendDriveOperationLog({
        operationType: 'spreadsheet_create',
        targetFileId: spreadsheetId,
        targetFileName: fileName,
        resultStatus: 'success',
        details: JSON.stringify({ folderId, sheetNames }),
      });
      return { spreadsheetId, sheetNameToId };
    } catch (error) {
      sendDriveOperationLog({
        operationType: 'spreadsheet_create',
        targetFileName: fileName,
        resultStatus: 'error',
        details: JSON.stringify({ error: error instanceof Error ? error.message : 'Unknown error', folderId, sheetNames }),
      });
      throw error;
    }
  }

  /**
   * シート追加
   * @param spreadsheetId スプレッドシートID
   * @param newSheetName 追加するシート名
   * @returns 処理後のシート名とシートIDの対応表（削除したデフォルトシートは含まない）
   */
  public addSheets = async (spreadsheetId: string, newSheetNames: string[]): Promise<Record<string, number>> => {
    try {
      // 現在のシート一覧を取得
      let spreadsheet = await gapi.client.sheets.spreadsheets.get({
        spreadsheetId,
      });

      const existingSheets = spreadsheet.result.sheets?.map((s: any) => s.properties?.title) ?? [];

      // シート名→シートIDの対応表（呼び出し側でシート情報を再取得しなくて済むよう返却する）
      const sheetNameToId: Record<string, number> = {};
      for (const s of spreadsheet.result.sheets ?? []) {
        if (s.properties?.title && s.properties?.sheetId !== undefined) {
          sheetNameToId[s.properties.title] = s.properties.sheetId;
        }
      }

      const requests: any[] = [];

      // 追加が必要なシート名をフィルタリング
      const sheetsToAdd = newSheetNames.filter(
        (name) => !existingSheets.includes(name)
      );

      if (sheetsToAdd.length > 0) {
        sheetsToAdd.forEach((name, index) => {
          requests.push({
            addSheet: { properties: { title: name, index: index } },
          });
        });
      }

      // デフォルトシート（Google生成の初期シート名"Sheet1"「シート1」等）のみ削除対象。
      // 前方一致だと"SheetData"のような意図的なシート名まで誤検出するため、末尾が数字のみの完全一致パターンに限定する。
      const defaultSheetNamePattern = /^(Sheet|シート)\d*$/;
      const sheetToDel = spreadsheet.result.sheets?.find((s: any) => defaultSheetNamePattern.test(s.properties?.title || ""));

      if (sheetToDel) {
        requests.push({
          deleteSheet: { sheetId: sheetToDel.properties?.sheetId },
        });
      }

      if (requests.length > 0) {
        const response = await gapi.client.sheets.spreadsheets.batchUpdate({
          spreadsheetId,
          resource: {
            requests,
          },
        });

        console.log("Sheets added:", response);

        // 対応表を処理後の状態に更新（削除したシートを除外し、追加したシートを反映）
        if (sheetToDel?.properties?.title) {
          delete sheetNameToId[sheetToDel.properties.title];
        }
        for (const reply of response.result.replies ?? []) {
          const props = reply.addSheet?.properties;
          if (props?.title && props?.sheetId !== undefined) {
            sheetNameToId[props.title] = props.sheetId;
          }
        }
        sendDriveOperationLog({
          operationType: 'sheet_add',
          targetFileId: spreadsheetId,
          resultStatus: 'success',
          details: JSON.stringify({ addedSheets: sheetsToAdd }),
        });
      }
      return sheetNameToId;
    } catch (error) {
      console.error("Failed to add multiple sheets", error);
      sendDriveOperationLog({
        operationType: 'sheet_add',
        targetFileId: spreadsheetId,
        resultStatus: 'error',
        details: JSON.stringify({ error: error instanceof Error ? error.message : 'Unknown error' }),
      });
      throw error;
    }
  }

  /**
   * シート内の値をクリアする。
   * @param spreadsheetId スプレッドシートID
   * @param sheetName シート名
   */
  public clear = async (spreadsheetId: string, sheetName: string) => {
    try {
      // シート名に特殊文字（括弧、スペース等）が含まれる場合、シングルクォートで囲む
      const quotedName = sheetName.includes('(') || sheetName.includes(')') || sheetName.includes(' ')
        ? `'${sheetName}'`
        : sheetName;
      await gapi.client.sheets.spreadsheets.values.clear({
        spreadsheetId: spreadsheetId,
        range: `${quotedName}!A:ZZZ`,
        resource: {},
      });
      sendDriveOperationLog({
        operationType: 'sheet_clear',
        targetFileId: spreadsheetId,
        resultStatus: 'success',
        details: JSON.stringify({ sheetName }),
      });
    } catch (error) {
      sendDriveOperationLog({
        operationType: 'sheet_clear',
        targetFileId: spreadsheetId,
        resultStatus: 'error',
        details: JSON.stringify({ error: error instanceof Error ? error.message : 'Unknown error', sheetName }),
      });
      throw error;
    }
  }

  /**
   * 指定したファイルをGoogleドライブから削除する
   * @param fileId 削除するファイルのID
   */
  public deleteFile = async (fileId: string) => {
    try {
      await gapi.client.drive.files.delete({ fileId });
      sendDriveOperationLog({
        operationType: 'file_delete',
        targetFileId: fileId,
        resultStatus: 'success',
      });
    } catch (error) {
      sendDriveOperationLog({
        operationType: 'file_delete',
        targetFileId: fileId,
        resultStatus: 'error',
        details: JSON.stringify({ error: error instanceof Error ? error.message : 'Unknown error' }),
      });
      throw error;
    }
  }

  /**
   * 指定したシート名の配列順にシートを並べ替える。
   * 配列に含まれないシートは末尾に維持される。
   * @param spreadsheetId スプレッドシートID
   * @param sheetNames 並べ替え後の順序でシート名を指定する配列
   */
  public async reorderSheets(spreadsheetId: string, sheetNames: string[]) {
    try {
      const spreadsheetInfo = await this.getSpreadSheetInfo(spreadsheetId);
      const sheets: any[] = spreadsheetInfo.sheets ?? [];

      // シート名からシート情報へのマップを作成
      const sheetMap = new Map<string, any>();
      for (const sheet of sheets) {
        sheetMap.set(sheet.properties.title, sheet);
      }

      const requests: any[] = [];

      // 指定された順序でindexを設定
      sheetNames.forEach((name, index) => {
        const sheet = sheetMap.get(name);
        if (sheet) {
          requests.push({
            updateSheetProperties: {
              properties: {
                sheetId: sheet.properties.sheetId,
                index: index,
              },
              fields: "index",
            },
          });
        }
      });

      if (requests.length > 0) {
        await gapi.client.sheets.spreadsheets.batchUpdate({
          spreadsheetId,
          resource: {
            requests,
          },
        });
        console.log("Sheets reordered:", sheetNames);
        sendDriveOperationLog({
          operationType: 'sheet_reorder',
          targetFileId: spreadsheetId,
          resultStatus: 'success',
          details: JSON.stringify({ sheetNames }),
        });
      }
    } catch (error) {
      console.error("Failed to reorder sheets:", error);
      sendDriveOperationLog({
        operationType: 'sheet_reorder',
        targetFileId: spreadsheetId,
        resultStatus: 'error',
        details: JSON.stringify({ error: error instanceof Error ? error.message : 'Unknown error' }),
      });
      throw error;
    }
  }

  /**
   * 指定したセル範囲に日付範囲のデータ入力規則を設定する。
   * 指定した日付範囲外の値は入力を拒否する。
   * @param spreadsheetId スプレッドシートID
   * @param validationRanges 日付範囲入力規則の設定配列
   * @example
   * await googleAPI.spreadSheetSetDateValidation(spreadsheetId, [{
   *   sheetId: 123456789,
   *   startRow: 3,
   *   endRow: 10,
   *   startCol: 6,
   *   endCol: 6,
   *   startDate: "2000-01-01",
   *   endDate: "2100-12-31",
   *   helpText: "2000-01-01～2100-12-31の範囲内で入力してください。"
   * }]);
   */
  public async spreadSheetSetDateValidation(spreadsheetId: string, validationRanges: DateValidationRange[]) {
    const requests: any[] = validationRanges.map((validationRange) => {
      return {
        setDataValidation: {
          range: {
            sheetId: validationRange.sheetId,
            startRowIndex: validationRange.startRow - 1,
            endRowIndex: validationRange.endRow,
            startColumnIndex: validationRange.startCol - 1,
            endColumnIndex: validationRange.endCol,
          },
          rule: {
            condition: {
              type: "DATE_BETWEEN",
              values: [
                { userEnteredValue: validationRange.startDate },
                { userEnteredValue: validationRange.endDate },
              ],
            },
            inputMessage: validationRange.helpText,
            strict: true,
            showCustomUi: true,
          },
        },
      };
    });

    if (requests.length > 0) {
      await gapi.client.sheets.spreadsheets.batchUpdate({
        spreadsheetId,
        resource: {
          requests,
        },
      });
    }
  }

  /**
   * 指定したセル範囲にデータ入力規則（プルダウン）を設定する。
   * 複数の範囲に対して一括で設定可能。
   * @param spreadsheetId スプレッドシートID
   * @param validationRanges データ入力規則の設定配列
   * @example
   * // プルダウンを設定
   * await googleAPI.spreadSheetSetDataValidation(spreadsheetId, [{
   *   sheetId: 123456789,
   *   startRow: 2,
   *   endRow: 10,
   *   startCol: 1,
   *   endCol: 1,
   *   values: ["選択肢1", "選択肢2", "選択肢3"],
   *   strict: true,
   *   showDropdown: true
   * }]);
   */
  public async spreadSheetSetDataValidation(spreadsheetId: string, validationRanges: DataValidationRange[]) {
    // batchUpdateのリクエストを作成
    const requests: any[] = validationRanges.map((validationRange) => {
      // プルダウンの選択肢をConditionValueの形式に変換
      const conditionValues = validationRange.values.map(value => ({
        userEnteredValue: value,
      }));

      return {
        setDataValidation: {
          range: {
            sheetId: validationRange.sheetId,
            startRowIndex: validationRange.startRow - 1,    // 0始まりに変換
            endRowIndex: validationRange.endRow,            // endは含まないので+1不要
            startColumnIndex: validationRange.startCol - 1,
            endColumnIndex: validationRange.endCol,
          },
          rule: {
            condition: {
              type: "ONE_OF_LIST",
              values: conditionValues,
            },
            showCustomUi: validationRange.showDropdown !== false,  // デフォルトtrue
            strict: validationRange.strict !== false,              // デフォルトtrue
          },
        },
      };
    });

    if (requests.length > 0) {
      await this.sendBatchUpdate(spreadsheetId, requests);
    }
  }

  /**
   * 指定したセル範囲にドロップダウン（データ検証）を設定する。
   * @param spreadsheetId スプレッドシートID
   * @param sheetId シートID（0が最初のシート）
   * @param startRowIndex 開始行インデックス（0始まり）
   * @param endRowIndex 終了行インデックス（0始まり、排他的）
   * @param startColumnIndex 開始列インデックス（0始まり）
   * @param endColumnIndex 終了列インデックス（0始まり、排他的）
   * @param values ドロップダウンの選択肢リスト
   */
  public async setDataValidationDropdown(
    spreadsheetId: string,
    sheetId: number,
    startRowIndex: number,
    endRowIndex: number,
    startColumnIndex: number,
    endColumnIndex: number,
    values: string[]
  ): Promise<void> {
    const requests = [
      {
        setDataValidation: {
          range: {
            sheetId: sheetId,
            startRowIndex: startRowIndex,
            endRowIndex: endRowIndex,
            startColumnIndex: startColumnIndex,
            endColumnIndex: endColumnIndex,
          },
          rule: {
            condition: {
              type: "ONE_OF_LIST",
              values: values.map((v) => ({ userEnteredValue: v })),
            },
            showCustomUi: true,
            strict: false,
          },
        },
      },
    ];

    if (requests.length > 0) {
      await gapi.client.sheets.spreadsheets.batchUpdate({
        spreadsheetId: spreadsheetId,
        resource: {
          requests: requests,
        },
      });
    }
  }

  /**
   * 指定したシート全体の書式（本システムで設定する項目）と入力規則を初期状態に戻す。
   * 既存ファイルを使い回す画面で、列構成の変更前に設定した書式・入力規則が残らないようにするために使用する。
   * ※値の書き込みより前に呼ぶこと（USER_ENTEREDで自動設定された日付等の表示形式まで消えるため）。
   * ※GoogleAPIBatchCollector に溜めず即時送信する（溜めると値の書き込みより後に送られるため）。
   * @param spreadsheetId スプレッドシートID
   * @param sheetIds 対象シートIDの配列
   */
  public async resetSheetFormatAndValidation(spreadsheetId: string, sheetIds: number[]): Promise<void> {
    const requests: any[] = sheetIds.map((sheetId) => ({
      repeatCell: {
        range: { sheetId },
        cell: {},
        // spreadSheetSetCellFormat で設定する項目 + 入力規則のみ初期化（罫線等は対象外）
        fields: [
          "userEnteredFormat.numberFormat",
          "userEnteredFormat.backgroundColor",
          "userEnteredFormat.textFormat",
          "userEnteredFormat.horizontalAlignment",
          "userEnteredFormat.verticalAlignment",
          "userEnteredFormat.wrapStrategy",
          "dataValidation",
        ].join(","),
      },
    }));

    if (requests.length > 0) {
      await gapi.client.sheets.spreadsheets.batchUpdate({
        spreadsheetId,
        resource: {
          requests,
        },
      });
    }
  }

  /**
   * 指定したスプレッドシートの全シートからデータ入力規則をすべて削除する。
   * @param spreadsheetId スプレッドシートID
   */
  public async clearAllDataValidation(spreadsheetId: string): Promise<void> {
    // スプレッドシート情報を取得して全シートのIDを得る
    const spreadsheetInfo = await this.getSpreadSheetInfo(spreadsheetId);
    const sheets: any[] = spreadsheetInfo.sheets ?? [];

    // 各シートに対してデータ入力規則を削除するリクエストを作成
    const requests: any[] = sheets.map((sheet: any) => ({
      setDataValidation: {
        range: {
          sheetId: sheet.properties.sheetId,
        },
        rule: undefined as any,
      },
    }));

    if (requests.length > 0) {
      await gapi.client.sheets.spreadsheets.batchUpdate({
        spreadsheetId,
        resource: {
          requests,
        },
      });
    }
  }

  /**
   * 指定した列の幅を設定する
   * @param spreadsheetId スプレッドシートID
   * @param sheetId シートID（0始まり）
   * @param startColumnIndex 開始列インデックス（0始まり、A=0）
   * @param endColumnIndex 終了列インデックス（0始まり、含まない）
   * @param pixelSize 列幅（ピクセル）
   */
  public async setColumnWidth(
    spreadsheetId: string,
    sheetId: number,
    startColumnIndex: number,
    endColumnIndex: number,
    pixelSize: number
  ): Promise<void> {
    const requests = [
      {
        updateDimensionProperties: {
          range: {
            sheetId: sheetId,
            dimension: "COLUMNS",
            startIndex: startColumnIndex,
            endIndex: endColumnIndex,
          },
          properties: {
            pixelSize: pixelSize,
          },
          fields: "pixelSize",
        },
      },
    ];

    await this.sendBatchUpdate(spreadsheetId, requests);
  }

  /**
   * 指定した行の高さを設定する。
   * @param spreadsheetId スプレッドシートID
   * @param sheetId シートID（0始まり）
   * @param startRowIndex 開始行インデックス（0始まり）
   * @param endRowIndex 終了行インデックス（0始まり、含まない）
   * @param pixelSize 行の高さ（ピクセル）
   */
  public async setRowHeight(
    spreadsheetId: string,
    sheetId: number,
    startRowIndex: number,
    endRowIndex: number,
    pixelSize: number
  ): Promise<void> {
    const requests = [
      {
        updateDimensionProperties: {
          range: {
            sheetId: sheetId,
            dimension: "ROWS",
            startIndex: startRowIndex,
            endIndex: endRowIndex,
          },
          properties: {
            pixelSize: pixelSize,
          },
          fields: "pixelSize",
        },
      },
    ];

    await gapi.client.sheets.spreadsheets.batchUpdate({
      spreadsheetId: spreadsheetId,
      resource: {
        requests: requests,
      },
    });
  }

  /**
   * 指定したセル範囲に、他セル範囲を選択肢として参照するデータ入力規則（ONE_OF_RANGE）を設定する。
   * 参照元の値が変わってもプルダウンの選択肢に自動反映される。
   * @param spreadsheetId スプレッドシートID
   * @param validationRanges ONE_OF_RANGE入力規則の設定配列
   * @example
   * // C28:E28の会社名をF29:F39のプルダウン選択肢として参照
   * await googleAPI.spreadSheetSetRangeValidation(spreadsheetId, [{
   *   sheetId: 123456789,
   *   startRow: 29,
   *   endRow: 39,
   *   startCol: 6,
   *   endCol: 6,
   *   sourceRange: "SheetData!$C$28:$E$28",
   * }]);
   */
  public async spreadSheetSetRangeValidation(spreadsheetId: string, validationRanges: RangeValidationRange[]) {
    const requests: any[] = validationRanges.map((validationRange) => {
      return {
        setDataValidation: {
          range: {
            sheetId: validationRange.sheetId,
            startRowIndex: validationRange.startRow - 1,
            endRowIndex: validationRange.endRow,
            startColumnIndex: validationRange.startCol - 1,
            endColumnIndex: validationRange.endCol,
          },
          rule: {
            condition: {
              type: "ONE_OF_RANGE",
              // ONE_OF_RANGEのuserEnteredValueは数式形式（"="始まり）で渡す必要がある
              values: [{ userEnteredValue: validationRange.sourceRange.startsWith("=") ? validationRange.sourceRange : `=${validationRange.sourceRange}` }],
            },
            showCustomUi: validationRange.showDropdown !== false,
            strict: validationRange.strict !== false,
          },
        },
      };
    });

    if (requests.length > 0) {
      await gapi.client.sheets.spreadsheets.batchUpdate({
        spreadsheetId,
        resource: {
          requests,
        },
      });
    }
  }

  /**
   * 指定したセル範囲に数値範囲のデータ入力規則を設定する。
   * 指定した数値範囲外の値は入力を拒否する。
   * @param spreadsheetId スプレッドシートID
   * @param validationRanges 数値範囲入力規則の設定配列
   * @example
   * await googleAPI.spreadSheetSetNumberValidation(spreadsheetId, [{
   *   sheetId: 123456789,
   *   startRow: 14,
   *   endRow: 14,
   *   startCol: 3,
   *   endCol: 3,
   *   min: 0,
   *   max: 999999999999,
   *   helpText: "0～999,999,999,999の範囲内で入力してください。"
   * }]);
   */
  public async spreadSheetSetNumberValidation(spreadsheetId: string, validationRanges: NumberValidationRange[]) {
    const requests: any[] = validationRanges.map((validationRange) => {
      return {
        setDataValidation: {
          range: {
            sheetId: validationRange.sheetId,
            startRowIndex: validationRange.startRow - 1,
            endRowIndex: validationRange.endRow,
            startColumnIndex: validationRange.startCol - 1,
            endColumnIndex: validationRange.endCol,
          },
          rule: {
            condition: {
              type: "NUMBER_BETWEEN",
              values: [
                { userEnteredValue: String(validationRange.min) },
                { userEnteredValue: String(validationRange.max) },
              ],
            },
            inputMessage: validationRange.helpText,
            strict: true,
            showCustomUi: true,
          },
        },
      };
    });

    if (requests.length > 0) {
      await gapi.client.sheets.spreadsheets.batchUpdate({
        spreadsheetId,
        resource: {
          requests,
        },
      });
    }
  }

  /**
   * 指定したセル範囲に、カスタム数式が真の場合に書式を適用する条件付き書式を設定する。
   * 非該当行の自動グレーアウト等に使用する。
   * @param spreadsheetId スプレッドシートID
   * @param formatRanges 条件付き書式の設定配列
   * @example
   * // C8（内製検討）が「なし」以外のとき、9行目をグレーアウト
   * await googleAPI.spreadSheetAddConditionalFormat(spreadsheetId, [{
   *   sheetId: 123456789,
   *   startRow: 9,
   *   endRow: 9,
   *   startCol: 1,
   *   endCol: 6,
   *   customFormula: '=$C$8<>"なし"',
   *   backgroundColor: { red: 0.9, green: 0.9, blue: 0.9 },
   *   textColor: { red: 0.6, green: 0.6, blue: 0.6 },
   * }]);
   */
  public async spreadSheetAddConditionalFormat(spreadsheetId: string, formatRanges: ConditionalFormatRange[]) {
    const requests: any[] = formatRanges.map((formatRange) => {
      const format: any = {};
      if (formatRange.backgroundColor) {
        format.backgroundColor = formatRange.backgroundColor;
      }
      if (formatRange.textColor) {
        format.textFormat = { foregroundColor: formatRange.textColor };
      }

      return {
        addConditionalFormatRule: {
          rule: {
            ranges: [{
              sheetId: formatRange.sheetId,
              startRowIndex: formatRange.startRow - 1,
              endRowIndex: formatRange.endRow,
              startColumnIndex: formatRange.startCol - 1,
              endColumnIndex: formatRange.endCol,
            }],
            booleanRule: {
              condition: {
                type: "CUSTOM_FORMULA",
                values: [{ userEnteredValue: formatRange.customFormula }],
              },
              format,
            },
          },
          index: 0,
        },
      };
    });

    if (requests.length > 0) {
      await gapi.client.sheets.spreadsheets.batchUpdate({
        spreadsheetId,
        resource: {
          requests,
        },
      });
    }
  }

  /**
   * 指定したセル範囲に警告付き保護（編集時に警告を表示するが、強制ロックはしない）を設定する。
   * @param spreadsheetId スプレッドシートID
   * @param protectedRanges 保護範囲の設定配列
   * @example
   * await googleAPI.spreadSheetAddProtectedRange(spreadsheetId, [{
   *   sheetId: 123456789,
   *   startRow: 1,
   *   endRow: 42,
   *   startCol: 1,
   *   endCol: 1,
   *   warningDescription: "No.列は読み取りキーのため編集しないでください。"
   * }]);
   */
  public async spreadSheetAddProtectedRange(spreadsheetId: string, protectedRanges: ProtectedRangeConfig[]) {
    const requests: any[] = protectedRanges.map((protectedRange) => {
      return {
        addProtectedRange: {
          protectedRange: {
            range: {
              sheetId: protectedRange.sheetId,
              startRowIndex: protectedRange.startRow - 1,
              endRowIndex: protectedRange.endRow,
              startColumnIndex: protectedRange.startCol - 1,
              endColumnIndex: protectedRange.endCol,
            },
            description: protectedRange.warningDescription,
            warningOnly: true,
          },
        },
      };
    });

    if (requests.length > 0) {
      await gapi.client.sheets.spreadsheets.batchUpdate({
        spreadsheetId,
        resource: {
          requests,
        },
      });
    }
  }

  /**
   * 指定した列を非表示にする（ユーザーが再表示可能な非表示、hiddenByUser）。
   * @param spreadsheetId スプレッドシートID
   * @param sheetId シートID（0始まり）
   * @param startColumnIndex 開始列インデックス（0始まり、A=0）
   * @param endColumnIndex 終了列インデックス（0始まり、含まない）
   */
  public async hideColumns(
    spreadsheetId: string,
    sheetId: number,
    startColumnIndex: number,
    endColumnIndex: number
  ): Promise<void> {
    const requests = [
      {
        updateDimensionProperties: {
          range: {
            sheetId: sheetId,
            dimension: "COLUMNS",
            startIndex: startColumnIndex,
            endIndex: endColumnIndex,
          },
          properties: {
            hiddenByUser: true,
          },
          fields: "hiddenByUser",
        },
      },
    ];

    await gapi.client.sheets.spreadsheets.batchUpdate({
      spreadsheetId: spreadsheetId,
      resource: {
        requests: requests,
      },
    });
  }

  /**
   * 指定したシートを非表示にする。
   * @param spreadsheetId スプレッドシートID
   * @param sheetId シートID
   */
  public async hideSheet(spreadsheetId: string, sheetId: number): Promise<void> {
    const requests = [
      {
        updateSheetProperties: {
          properties: {
            sheetId: sheetId,
            hidden: true,
          },
          fields: "hidden",
        },
      },
    ];

    await gapi.client.sheets.spreadsheets.batchUpdate({
      spreadsheetId: spreadsheetId,
      resource: {
        requests: requests,
      },
    });
  }
}

/**
 * 書き込み系のAPI呼び出しを即時送信せずに溜め、flush() でまとめて送信するクラス。
 * 対象：clear / spreadSheetSetValue(Range) / spreadsheets.batchUpdate を使うメソッド
 *       （spreadSheetSetCellFormat / spreadSheetSetFormulas / spreadSheetSetDataValidation / setColumnWidth）
 * 対象スプレッドシート以外への呼び出しは、通常どおり即時送信する。
 * flush() の送信順：値クリア → 値書き込み → batchUpdate（溜めた順）。
 * 同一シートに対する「クリア → 値 → 書式等」の順序は、個別送信時と同じになる。
 */
export class GoogleAPIBatchCollector extends GoogleAPI {
  private readonly targetSpreadsheetId: string;
  private clearRanges: string[] = [];
  private valueRanges: { range: string; values: any[][] }[] = [];
  private requests: any[] = [];
  // 基底クラスの clear（アロー関数プロパティのため super で参照できない）を退避
  private readonly baseClear = this.clear;

  constructor(targetSpreadsheetId: string) {
    super();
    this.targetSpreadsheetId = targetSpreadsheetId;
  }

  public clear = async (spreadsheetId: string, sheetName: string) => {
    if (spreadsheetId !== this.targetSpreadsheetId) {
      return this.baseClear(spreadsheetId, sheetName);
    }
    this.clearRanges.push(`${quoteSheetName(sheetName)}!A:ZZZ`);
  };

  public async spreadSheetSetValueRange(spreadsheetId: string, range: string, values: any[][]) {
    if (spreadsheetId !== this.targetSpreadsheetId) {
      return super.spreadSheetSetValueRange(spreadsheetId, range, values);
    }
    this.valueRanges.push({ range, values });
  }

  protected async sendBatchUpdate(spreadsheetId: string, requests: any[]): Promise<void> {
    if (spreadsheetId !== this.targetSpreadsheetId) {
      return super.sendBatchUpdate(spreadsheetId, requests);
    }
    this.requests.push(...requests);
  }

  /**
   * 溜めた呼び出しをまとめて送信する（最大3回のAPI呼び出し）。
   */
  public async flush(): Promise<void> {
    const spreadsheetId = this.targetSpreadsheetId;
    const clearRanges = this.clearRanges;
    const valueRanges = this.valueRanges;
    const requests = this.requests;
    this.clearRanges = [];
    this.valueRanges = [];
    this.requests = [];

    try {
      if (clearRanges.length > 0) {
        await gapi.client.sheets.spreadsheets.values.batchClear({
          spreadsheetId,
          resource: { ranges: clearRanges },
        });
        sendDriveOperationLog({
          operationType: 'sheet_clear',
          targetFileId: spreadsheetId,
          resultStatus: 'success',
          details: JSON.stringify({ ranges: clearRanges }),
        });
      }
      if (valueRanges.length > 0) {
        await gapi.client.sheets.spreadsheets.values.batchUpdate({
          spreadsheetId,
          resource: {
            valueInputOption: "USER_ENTERED",
            data: valueRanges,
          },
        });
        sendDriveOperationLog({
          operationType: 'spreadsheet_write',
          targetFileId: spreadsheetId,
          resultStatus: 'success',
          details: JSON.stringify({ ranges: valueRanges.map(v => ({ range: v.range, rows: v.values.length, cols: v.values[0]?.length ?? 0 })) }),
        });
      }
      if (requests.length > 0) {
        await super.sendBatchUpdate(spreadsheetId, requests);
      }
    } catch (error) {
      sendDriveOperationLog({
        operationType: 'spreadsheet_write',
        targetFileId: spreadsheetId,
        resultStatus: 'error',
        details: JSON.stringify({ error: error instanceof Error ? error.message : 'Unknown error', batch: true }),
      });
      throw error;
    }
  }
}
